import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { format, parse } from 'date-fns';
import DatePicker from 'react-datepicker';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select } from '../ui/newSelect';
import { Alert, AlertDescription } from '../ui/alert';
import { Spinner } from '../ui/spinner';
import { Actions as CONSULTANT_ACTIONS } from '../../store/actions/consultantClientsActions';
import { toggleAlertMessage } from '../../store/action';
import { friendlyConsultantClientError, planTypeLabel } from '../utils/consultantClientUtils';

/**
 * CYF admin editor for consultant end-client billing schedule (commitment end date, per-report fee).
 */
const ConsultantClientScheduleAdmin = ({
  organisation,
  cyfAdmin,
  getAdminConsultantClientSchedule,
  patchAdminConsultantClientSchedule,
  showAlert,
}) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [schedule, setSchedule] = useState(null);
  const [masterPerReportFeeVal, setMasterPerReportFeeVal] = useState(null);
  const [form, setForm] = useState({
    planType: 'per_report',
    perReportFeeVal: '',
    commitmentEndDate: '',
    autoRenew: true,
  });

  const isConsultantClient =
    cyfAdmin &&
    organisation?.id &&
    organisation?.consultantId &&
    organisation.consultantId !== 'NONE' &&
    String(organisation.consultantId) !== String(organisation.id);

  useEffect(() => {
    if (!isConsultantClient) {
      setLoading(false);
      return;
    }

    setLoading(true);
    getAdminConsultantClientSchedule(organisation.id).then((result) => {
      setLoading(false);
      if (!result.ok) return;
      const row = result.data?.schedule;
      setSchedule(row);
      setMasterPerReportFeeVal(result.data?.masterPerReportFeeVal ?? null);
      const endDate = row?.commitmentEndDate || result.data?.subscriptionEndDate || '';
      setForm({
        planType: row?.planType || 'per_report',
        perReportFeeVal: row?.perReportFeeVal != null ? String(row.perReportFeeVal) : '',
        commitmentEndDate: endDate,
        autoRenew: row?.autoRenew !== false,
      });
    });
  }, [organisation?.id, isConsultantClient, getAdminConsultantClientSchedule]);

  if (!isConsultantClient) return null;

  const onSave = async () => {
    setSaving(true);
    const body = {
      planType: form.planType,
      perReportFeeVal: form.perReportFeeVal !== '' ? Number(form.perReportFeeVal) : null,
      commitmentEndDate: form.commitmentEndDate || null,
      autoRenew: form.autoRenew,
    };
    const result = await patchAdminConsultantClientSchedule(organisation.id, body);
    setSaving(false);
    if (result.ok) {
      setSchedule(result.data?.schedule || schedule);
      showAlert(true, 'Consultant client schedule updated.', 'success', 4000);
    } else {
      showAlert(true, friendlyConsultantClientError(result.error || result.message), 'error', 6000);
    }
  };

  const commitmentDateValue =
    form.commitmentEndDate && form.commitmentEndDate !== ''
      ? parse(form.commitmentEndDate, 'yyyy-MM-dd', new Date())
      : null;

  return (
    <div className='mt-6 rounded-xl border border-border bg-card shadow-sm'>
      <header className='border-b border-border px-6 py-4'>
        <h2 className='text-lg font-semibold text-foreground'>Consultant billing schedule</h2>
        <p className='text-xs text-muted-foreground mt-1'>
          End-client pricing and commitment end date (CYF admin only). Consultants cannot edit expiry dates.
        </p>
      </header>
      <div className='p-6'>
        {loading ? (
          <div className='flex justify-center py-6'>
            <Spinner className='h-6 w-6' />
          </div>
        ) : !schedule ? (
          <Alert>
            <AlertDescription className='text-sm'>
              No billing schedule row yet. The consultant can onboard this client from Client Portfolio, or run the
              legacy migration script.
              {masterPerReportFeeVal != null && (
                <span className='block mt-1'>Master per-report fee: £{masterPerReportFeeVal}</span>
              )}
            </AlertDescription>
          </Alert>
        ) : (
          <Card className='bg-card border-border'>
            <CardHeader>
              <CardTitle className='text-base'>{planTypeLabel(form.planType)}</CardTitle>
            </CardHeader>
            <CardContent className='space-y-4'>
              <div>
                <Label>Plan type</Label>
                <Select
                  value={form.planType}
                  onValueChange={(v) => setForm((f) => ({ ...f, planType: v }))}
                  options={[
                    { value: 'per_report', label: 'Per report' },
                    { value: 'client_subscription', label: 'Client subscription' },
                  ]}
                />
              </div>
              {form.planType === 'per_report' && (
                <div>
                  <Label>Per-report fee</Label>
                  <Input
                    type='number'
                    min='0'
                    step='0.01'
                    value={form.perReportFeeVal}
                    onChange={(e) => setForm((f) => ({ ...f, perReportFeeVal: e.target.value }))}
                  />
                </div>
              )}
              <div>
                <Label>Commitment end date</Label>
                <DatePicker
                  selected={commitmentDateValue}
                  onChange={(date) =>
                    setForm((f) => ({
                      ...f,
                      commitmentEndDate: date ? format(date, 'yyyy-MM-dd') : '',
                    }))
                  }
                  dateFormat='dd/MM/yyyy'
                  className='h-9 w-full max-w-xs rounded-lg border border-input bg-transparent px-3 py-2 text-sm'
                  placeholderText='Select end date'
                />
                <p className='text-xs text-muted-foreground mt-1'>
                  Also mirrored to org subscription end date. Drives T−60 / T−30 renewal reminders.
                </p>
              </div>
              <div>
                <Label>Auto-renew</Label>
                <p className='text-sm text-foreground'>{form.autoRenew ? 'Yes' : 'No'}</p>
              </div>
              <Button loading={saving} onClick={onSave}>
                Save schedule
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  cyfAdmin: state.profile?.details?.cyfAdmin,
});

const mapDispatchToProps = (dispatch) => ({
  getAdminConsultantClientSchedule: (orgId) => dispatch(CONSULTANT_ACTIONS.getAdminConsultantClientSchedule(orgId)),
  patchAdminConsultantClientSchedule: (orgId, body) =>
    dispatch(CONSULTANT_ACTIONS.patchAdminConsultantClientSchedule(orgId, body)),
  showAlert: (status, message, severity, hideDuration) =>
    dispatch(toggleAlertMessage(status, message, severity, hideDuration)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ConsultantClientScheduleAdmin);
